// Home-header greeting bits — time-of-day greeting, first-name derivation, and a
// rotating catchphrase. Pure and UI-agnostic so RN and web share one source.

// "Good morning" (<12), "Good afternoon" (<18), else "Good evening".
export function timeGreeting(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// i18n key for the time-of-day greeting — screens render `t(timeGreetingKey())`
// so the greeting localises while the time logic stays here.
export function timeGreetingKey(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return "home.goodMorning";
  if (hour < 18) return "home.goodAfternoon";
  return "home.goodEvening";
}

// First name for the greeting: the full name's first word, else the email's local
// part ("stewart" from stewart@…), else "there". Full names read too formal.
export function greetingName(fullName?: string | null, email?: string | null): string {
  const base = (fullName?.trim() || email?.split("@")[0] || "there").trim();
  return base.split(/\s+/)[0] || "there";
}

// The user's chosen display name, resolved from auth `user_metadata`. We prefer
// `display_name` (which the app writes on edit and which OAuth providers never
// set) over the provider-supplied `full_name` / `name`. GoTrue overwrites those
// provider fields from the identity token on every social sign-in, so relying on
// `full_name` for an editable name means it reverts to the Google/Apple name each
// login. `display_name` is untouched by providers, so an edited name sticks.
export function userDisplayName(
  meta?: {
    display_name?: string | null;
    full_name?: string | null;
    name?: string | null;
  } | null,
): string | undefined {
  const picked = meta?.display_name || meta?.full_name || meta?.name;
  return picked?.trim() || undefined;
}

export const CATCHPHRASES = [
  "What shall we cook?",
  "What should we make?",
  "What's on the menu?",
  "What are we baking?",
  "What can we create?",
  "What's for dinner?",
  "What shall we prepare?",
  "What are we cooking?",
  "What sounds good?",
  "What shall we whip up?",
  "What do we fancy?",
] as const;

// A random catchphrase. Call once per mount (memoize/useState) so it doesn't
// change on every re-render.
export function randomCatchphrase(): string {
  return CATCHPHRASES[Math.floor(Math.random() * CATCHPHRASES.length)];
}

// i18n keys for the catchphrases (one per CATCHPHRASES entry). Screens render
// `t(randomCatchphraseKey())` so the rotating title localises.
export const CATCHPHRASE_KEYS = CATCHPHRASES.map((_, i) => `home.catchphrase${i}`);

// A random catchphrase i18n key. Call once per mount (memoize/useState).
export function randomCatchphraseKey(): string {
  return CATCHPHRASE_KEYS[Math.floor(Math.random() * CATCHPHRASE_KEYS.length)];
}
